import {
    AutocompleteInput,
    Button,
    Datagrid,
    DateField,
    DateInput,
    EditButton,
    Link,
    List,
    NumberInput,
    ReferenceInput,
    ShowButton,
    TextField,
    TextInput,
    useCreatePath,
    useRecordContext,
    useResourceContext,
    useTranslate
} from "react-admin";

import { rowStyles } from "../../utils/rowStyles";
import { ListPagination } from "../../utils/components/ListPagination";

import { YesNoBooleanField } from "../../fields/YesNoBooleanField";
import { ProductListActions } from "./components/ProductListActions";
import { ContentStatusField } from "../../utils/components/ContentStatusField";
import { PublicationStatusField } from "../../utils/components/PublicationStatusField";
import { ContentStatusFilter } from "../../utils/components/form/ContentStatusFilter";
import { PublicationStatusFilter } from "../../utils/components/form/PublicationStatusFilter";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMemo } from "react";

const stopPropagation = (e: any) => e.stopPropagation();

export const DuplicateButton = (props: any) => {
    const t = useTranslate();
    const resource = useResourceContext(props);
    const record = useRecordContext(props);
    const createPath = useCreatePath();
    const redirectPath = useMemo(() => {
        return createPath({ resource, type: "create" }) + "?duplicate=" + record.id;
    }, [createPath, record]);

    return (
        <Button component={Link} to={redirectPath} onClick={stopPropagation} label={t("resources.common.buttons.duplicate")}>
            <ContentCopyIcon />
        </Button>
    );
};

export function ProductList(): JSX.Element {
    const t = useTranslate();

    const filters = [
        <TextInput key="filter-text" label={t("resources.product.fields.name")} source="searchTerms" alwaysOn />,
        <TextInput key="filter-text" label={t("resources.product.filters.codeEm")} source="codeEm" />,
        <TextInput key="filter-text" label={t("resources.product.filters.codeEmArte")} source="codeEmArte" />,
        <ReferenceInput key="filter-season" label={t("resources.product.fields.season")} source="seasonUuid" reference="season">
            <AutocompleteInput label={t("resources.product.fields.season")} optionValue="id" optionText="name" />
        </ReferenceInput>,
        <ReferenceInput key="filter-serie" label={t("resources.product.fields.serie")} source="serieUuid" reference="serie">
            <AutocompleteInput label={t("resources.product.fields.serie")} optionValue="id" optionText="name" />
        </ReferenceInput>,

        <ReferenceInput
            key="filter-contentType"
            label={t("resources.product.filters.contentType")}
            source="contentTypeUuid"
            reference="contentType"
        >
            <AutocompleteInput label={t("resources.product.filters.contentType")} optionValue="id" optionText="name" />
        </ReferenceInput>,

        <ReferenceInput
            key="filter-rightHolder"
            label={t("resources.product.filters.rightHolder")}
            source="rightHolderUuid"
            reference="rightHolder"
        >
            <AutocompleteInput label={t("resources.product.filters.rightHolder")} optionValue="id" optionText="name" />
        </ReferenceInput>,

        <ReferenceInput key="filter-format" label={t("resources.product.filters.format")} source="formatUuid" reference="format">
            <AutocompleteInput label={t("resources.product.filters.format")} optionValue="id" optionText="name" />
        </ReferenceInput>,

        <ContentStatusFilter source="status" label={t("resources.common.filters.status")} />,
        <PublicationStatusFilter source="publicationStatus" label={t("resources.common.filters.publicationStatus")} />,

        <NumberInput key="filter-rating-begin" label={t("resources.product.filters.rangeRatingBegin")} source="rangeRatingBegin" />,
        <NumberInput key="filter-rating-end" label={t("resources.product.filters.rangeRatingEnd")} source="rangeRatingEnd" />,

        <DateInput
            key="publication-from"
            source="rangePublicationDateBegin"
            label={t("resources.product.filters.rangePublicationDateBegin")}
        />,
        <DateInput key="publication-to" source="rangePublicationDateEnd" label={t("resources.product.filters.rangePublicationDateEnd")} />,
        <TextInput key="filter-id" label={t("resources.common.fields.id")} source="id" />
    ];

    return (
        <>
            <List
                pagination={<ListPagination />}
                perPage={20}
                sort={{ field: "createdAt", order: "DESC" }}
                filters={filters}
                actions={<ProductListActions />}
            >
                <Datagrid rowStyle={rowStyles}>
                    <TextField source="name" />
                    <TextField source="title" />
                    <TextField source="codeEm" />
                    <TextField source="codeEmArte" />
                    <TextField sortBy="rightHolder" source="rightHolder.name" label={t("resources.product.fields.rightHolder")} />
                    <TextField sortBy="contentType" source="contentType.name" label={t("resources.product.fields.contentType")} />
                    <ContentStatusField source="status" label={t("resources.common.fields.status")} />
                    <PublicationStatusField source="publicationStatus" label={t("resources.common.fields.publicationStatus")} />
                    <DateField label={t("resources.product.fields.publicationStartedAt")} source="publicationStartedAt" />
                    <DateField label={t("resources.product.fields.publicationEndedAt")} source="publicationEndedAt" />
                    <YesNoBooleanField source="singleContent" />
                    <DuplicateButton />
                    <EditButton />
                    <ShowButton label="" />
                </Datagrid>
            </List>
        </>
    );
}
